import React, { useState, useEffect } from 'react';
import { Modal, Descriptions, Rate, Tag, Timeline, Spin, Card, Empty } from 'antd';
import { ClockCircleOutlined, UserOutlined, ShoppingOutlined, CommentOutlined } from '@ant-design/icons';
import { fetchClient } from '../utils/fetch';
import moment from 'moment';

const NPSDetailsModal = ({ visible, onClose, record }) => {
  const [clientData, setClientData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (visible && record?.clientId) {
      loadClientData(record.clientId);
    }
  }, [visible, record]);

  const loadClientData = async (clientId) => {
    setIsLoading(true);
    try {
      const data = await fetchClient(clientId);
      setClientData(data);
    } catch (error) {
      console.error('Error loading client data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 4.5) return '#52c41a';
    if (score >= 3.5) return '#1890ff';
    if (score >= 2.5) return '#faad14';
    return '#ff4d4f';
  };

  const getAllNpsScores = () => {
    if (!clientData?.lead) return [];
    
    const allScores = [];
    clientData.lead.forEach(lead => {
      lead.application?.forEach(spec => {
        spec.orders?.forEach(order => {
          if (order.NpsScore && Array.isArray(order.NpsScore)) {
            order.NpsScore.forEach(nps => {
              allScores.push({
                ...nps,
                orderId: order._id,
                orderNumber: order.orderNumber,
              });
            });
          }
        });
      });
    });
    
    return allScores.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  if (!record) return null;

  return (
    <Modal
      title={
        <div style={{ fontSize: '20px', fontWeight: 600 }}>
          Детали NPS оценки
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
    >
      <Spin spinning={isLoading}>
        <Card bordered={false} style={{ marginBottom: 16, background: '#fafafa' }}>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ marginBottom: 16 }}>
              <Rate disabled value={record.score} style={{ fontSize: 32 }} />
            </div>
            <div style={{ fontSize: 36, fontWeight: 700, color: getScoreColor(record.score) }}>
              {record.score} / 5
            </div>
            {record.comment && (
              <div style={{ 
                marginTop: 16, 
                padding: '12px 16px',
                background: 'white',
                borderRadius: '8px',
                textAlign: 'left',
                fontSize: '14px',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              }}>
                <strong>Комментарий:</strong>
                <div style={{ marginTop: 8, color: '#595959' }}>
                  {record.comment}
                </div>
              </div>
            )}
          </div>
        </Card>

        <Descriptions bordered column={1} size="small">
          <Descriptions.Item 
            label={<span><ClockCircleOutlined /> Дата оценки</span>}
          >
            {moment(record.rawDate).format('DD MMMM YYYY, HH:mm')}
          </Descriptions.Item>
          
          <Descriptions.Item 
            label={<span><UserOutlined /> Клиент</span>}
          >
            {record.clientName}
          </Descriptions.Item>

          {record.orderId && (
            <Descriptions.Item 
              label={<span><ShoppingOutlined /> ID заказа</span>}
            >
              {record.orderId}
            </Descriptions.Item>
          )}

          {record.npsSessionId && (
            <Descriptions.Item 
              label={<span><CommentOutlined /> ID сессии</span>}
            >
              {record.npsSessionId}
            </Descriptions.Item>
          )}
        </Descriptions>

        {clientData && getAllNpsScores().length > 1 && (
          <Card 
            title={<span style={{ fontWeight: 600 }}>История оценок клиента</span>}
            bordered={false}
            style={{ marginTop: 24 }}
          >
            <Timeline mode="left">
              {getAllNpsScores().map((nps, index) => (
                <Timeline.Item
                  key={index}
                  color={getScoreColor(nps.score)}
                  label={moment(nps.date).format('DD.MM.YYYY HH:mm')}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Rate disabled value={nps.score} style={{ fontSize: 14 }} />
                    <span style={{ fontWeight: 600, fontSize: 16 }}>
                      {nps.score} / 5
                    </span>
                    {nps.orderNumber && (
                      <Tag color="blue" style={{ marginLeft: 8 }}>
                        Заказ №{nps.orderNumber}
                      </Tag>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        )}

        {clientData && getAllNpsScores().length === 0 && (
          <Card style={{ marginTop: 24 }}>
            <Empty description="Нет истории оценок" />
          </Card>
        )}
      </Spin>
    </Modal>
  );
};

export default NPSDetailsModal;

